import { useEffect } from "react"

type EarlyKey = Pick<
  KeyboardEventInit,
  "key" | "code" | "shiftKey" | "ctrlKey" | "metaKey" | "altKey"
> & { at: number }

declare global {
  interface Window {
    __earlyKeys?: { keys: EarlyKey[]; stop: () => void }
  }
}

/**
 * Records shortcut keys pressed before hydration, when no listener exists yet.
 * Must stay self-contained to be inlined as a script. Only printable keys and
 * $mod+K are kept, so keys the browser already acts on (Tab, Enter, arrows,
 * Space) don't run twice. Escape or a click drops what was typed, and so does
 * a pause longer than useKeybindings lets a sequence wait (1,000 ms), so a
 * replay can't join keys the live listeners would have kept apart.
 */
export function bufferEarlyKeys() {
  const keys: EarlyKey[] = []
  const record = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement
    if (
      event.repeat ||
      event.isComposing ||
      event.altKey ||
      target.isContentEditable ||
      /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)
    ) {
      return
    }
    if (event.key === "Escape") keys.length = 0
    else if (
      event.metaKey || event.ctrlKey
        ? event.key.toLowerCase() === "k"
        : event.key.length === 1 && event.key !== " "
    ) {
      const at = Date.now()
      if (keys.length && at - keys[keys.length - 1]!.at > 1000) keys.length = 0
      keys.push({
        at,
        key: event.key,
        code: event.code,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
      })
      if (keys.length > 8) keys.shift()
    }
  }
  const clear = () => {
    keys.length = 0
  }
  addEventListener("keydown", record, true)
  addEventListener("pointerdown", clear, true)
  window.__earlyKeys = {
    keys,
    stop() {
      removeEventListener("keydown", record, true)
      removeEventListener("pointerdown", clear, true)
    },
  }
}

export const earlyKeyBufferScript = `(${bufferEarlyKeys.toString()})()`

/**
 * When each replayed key was really pressed, so a sequence times out from the
 * press: a prefix typed long before hydration can't join a key typed after it.
 */
export const earlyKeyPressedAt = new WeakMap<Event, number>()

export function replayEarlyKeys() {
  const buffer = window.__earlyKeys
  if (!buffer) return
  buffer.stop()
  delete window.__earlyKeys
  for (const { at, ...key } of buffer.keys) {
    const event = new KeyboardEvent("keydown", { ...key, bubbles: true, cancelable: true })
    earlyKeyPressedAt.set(event, at)
    const handled = !(document.activeElement ?? document.body).dispatchEvent(event)
    // A handled shortcut may open UI that renders later; the rest would still
    // reach the page underneath it, so drop them.
    if (handled) break
  }
}

/**
 * Replays early keys once client-only shortcut owners have mounted. Render it
 * inside ClientOnly after the app shell so their effects run first; the timeout
 * lets renders those effects schedule settle.
 */
export function EarlyKeyReplay() {
  useEffect(() => {
    const timer = setTimeout(replayEarlyKeys)
    return () => clearTimeout(timer)
  }, [])
  return null
}
